import React, { useState, useEffect, useRef } from "react";
import {
  adminStats, adminOrders, adminContacts, updateOrderStatus, adminLogin, LOGO_URL,
  adminInventory, updateStock, updateQuantity, exportUrl, purgeTestData,
  ownerInventory,
} from "@/lib/api";
import { RefreshCw, DollarSign, Package, Users, LogOut, Eye, Lock, Bell, BellRing, BellOff, Check, X, Download, AlertTriangle, Trash2, Ticket, Boxes } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["new", "preparing", "ready", "out-for-delivery", "completed", "cancelled"];
const TOKEN_KEY = "stb_admin_token";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [ownerInv, setOwnerInv] = useState([]);
  const [invFilter, setInvFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [alertsOn, setAlertsOn] = useState(false);
  const seenPaidRef = useRef(null);

  const ding = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = 880;
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      o.start(); o.stop(ctx.currentTime + 0.5);
    } catch { /* ignore */ }
  };

  const pingNewOrders = (ordersList) => {
    const paid = ordersList.filter((o) => o.payment_status === "paid");
    const paidIds = new Set(paid.map((o) => o.id));
    if (seenPaidRef.current === null) {
      seenPaidRef.current = paidIds; // baseline — don't ping historical
      return;
    }
    const fresh = paid.filter((o) => !seenPaidRef.current.has(o.id));
    seenPaidRef.current = paidIds;
    fresh.forEach((o) => {
      ding();
      toast.success(`💰 New paid order — ${o.customer_name} · $${o.total?.toFixed(2)}`, { duration: 8000 });
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        try {
          new Notification("🍭 New paid order!", {
            body: `${o.customer_name} · $${o.total?.toFixed(2)} · ${o.fulfillment || "pickup"}`,
            icon: LOGO_URL,
            tag: o.id,
          });
        } catch { /* ignore */ }
      }
    });
  };

  const enableAlerts = async () => {
    if (typeof Notification === "undefined") {
      toast.error("This browser doesn't support notifications");
      return;
    }
    const perm = await Notification.requestPermission();
    if (perm === "granted") {
      setAlertsOn(true);
      toast.success("Order alerts on — you'll get a ping on every new paid order 🔔");
    } else {
      toast.error("Notifications blocked — enable them in your browser settings");
    }
  };

  const load = async (tok) => {
    setLoading(true);
    try {
      const [s, o, inv, oi] = await Promise.all([adminStats(tok), adminOrders(tok), adminInventory(tok), ownerInventory(tok)]);
      setStats(s);
      setOrders(o);
      setInventory(inv);
      setOwnerInv(oi?.groups || []);
      pingNewOrders(o);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired — please log in again.");
        doLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  // Poll for new paid orders every 25s (free order-alert popup)
  useEffect(() => {
    if (!authed || !token) return;
    setAlertsOn(typeof Notification !== "undefined" && Notification.permission === "granted");
    const iv = setInterval(async () => {
      try {
        const o = await adminOrders(token);
        setOrders(o);
        pingNewOrders(o);
      } catch { /* ignore */ }
    }, 25000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, token]);

  const setQty = async (productId, value) => {
    const quantity = value === "" || value === null ? null : Math.max(0, parseInt(value, 10) || 0);
    setInventory((prev) => prev.map((p) => (p.id === productId ? { ...p, quantity, auto: quantity !== null } : p)));
    try {
      const res = await updateQuantity(token, productId, quantity);
      setInventory((prev) => prev.map((p) => (p.id === productId ? { ...p, stock_status: res.stock_status, waiting: res.emailed > 0 ? 0 : p.waiting } : p)));
      if (res?.emailed > 0) toast.success(`Restocked — ${res.emailed} customer${res.emailed > 1 ? "s" : ""} emailed! 📧`);
      else if (quantity !== null) toast.success(`Set to ${quantity} in stock (auto)`);
      else toast.success("Quantity cleared — back to manual");
    } catch {
      toast.error("Could not update quantity");
    }
  };

  const setOwnerQty = async (id, value) => {
    const quantity = Math.max(0, parseInt(value, 10) || 0);
    setOwnerInv((prev) =>
      prev.map((g) => ({
        ...g,
        items: g.items.map((it) => (it.id === id ? { ...it, quantity } : it)),
        total: g.items.reduce((s, it) => s + (it.id === id ? quantity : it.quantity), 0),
      }))
    );
    try {
      await updateQuantity(token, id, quantity);
    } catch {
      toast.error("Could not update stock count");
    }
  };

  const setStock = async (productId, status) => {
    const prevItem = inventory.find((p) => p.id === productId);
    setInventory((prev) => prev.map((p) => (p.id === productId ? { ...p, stock_status: status } : p)));
    try {
      const res = await updateStock(token, productId, status);
      if (res?.emailed > 0) {
        toast.success(`Back in stock — ${res.emailed} customer${res.emailed > 1 ? "s" : ""} emailed! 📧`);
      } else {
        toast.success(status === "out" ? "Marked sold out" : status === "low" ? "Marked low stock" : "Marked in stock");
      }
      if (status !== "out") {
        setInventory((prev) => prev.map((p) => (p.id === productId ? { ...p, waiting: 0 } : p)));
      }
    } catch {
      toast.error("Could not update stock");
      setInventory((prev) => prev.map((p) => (p.id === productId ? { ...p, stock_status: prevItem?.stock_status || "in" } : p)));
    }
  };

  useEffect(() => {
    if (authed && token) load(token);
  }, [authed, token]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await adminLogin(password);
      localStorage.setItem(TOKEN_KEY, res.token);
      setToken(res.token);
      setAuthed(true);
      setPassword("");
      toast.success("Welcome back, Bandit! 🦝");
    } catch {
      toast.error("Incorrect password");
    } finally {
      setLoggingIn(false);
    }
  };

  const doLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setStats(null);
    setOrders([]);
    setInventory([]);
  };

  const viewStorefront = () => {
    localStorage.setItem("stb_preview", "1");
    window.open("/?preview=1", "_blank");
  };

  const changeStatus = async (orderId, status) => {
    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
    try {
      const res = await updateOrderStatus(token, orderId, status);
      if (res?.customer_emailed) toast.success(`Marked "${status}" — customer emailed 📧`);
      else toast.success(`Order marked "${status}"`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const [purging, setPurging] = useState(false);
  const doPurge = async () => {
    if (!window.confirm("Delete ALL orders, messages and test data? This cannot be undone.\n\nUse this once before launch to start with a clean slate.")) return;
    setPurging(true);
    try {
      const res = await purgeTestData(token);
      const n = Object.values(res?.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Cleared ${n} test record${n === 1 ? "" : "s"} — you're starting fresh! 🧹`);
      load(token);
    } catch {
      toast.error("Could not clear data — please try again.");
    } finally {
      setPurging(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-candy-bg flex items-center justify-center p-6">
        <form
          onSubmit={doLogin}
          data-testid="admin-login-form"
          className="bg-white border-4 border-black rounded-2xl shadow-brutal p-8 w-full max-w-sm text-candy-night"
        >
          <img src={LOGO_URL} alt="logo" className="h-20 w-20 rounded-full mx-auto mb-4 drop-shadow-[3px_3px_0_rgba(0,0,0,0.25)]" />
          <h1 className="font-display text-3xl text-center mb-1">OWNER LOGIN</h1>
          <p className="text-center text-sm text-candy-night/70 font-medium mb-6">The Sweet Tooth Bandit — Founder access</p>
          <label className="font-mono text-xs uppercase font-bold flex items-center gap-2 mb-1">
            <Lock className="h-4 w-4" /> Password
          </label>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            className="w-full border-4 border-black rounded-xl px-3 py-2.5 font-medium focus:outline-none focus:shadow-brutal-teal transition-shadow mb-4"
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loggingIn}
            className="w-full bg-candy-pink text-white font-bold uppercase tracking-wider py-3 border-4 border-black rounded-xl shadow-brutal-sm active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform duration-150 disabled:opacity-60"
          >
            {loggingIn ? "Logging in…" : "Log In"}
          </button>
        </form>
      </div>
    );
  }

  const statCards = [
    { icon: DollarSign, label: "Revenue", value: `$${stats?.revenue?.toFixed(2) ?? "0.00"}`, bg: "bg-candy-gold" },
    { icon: Package, label: "Total Orders", value: stats?.total_orders ?? 0, bg: "bg-candy-pink text-white" },
    { icon: Users, label: "Waiting List", value: stats?.subscribers ?? 0, bg: "bg-white" },
  ];

  return (
    <div className="min-h-screen bg-candy-bg p-4 sm:p-8 text-candy-night" data-testid="admin-dashboard">
      <header className="flex flex-wrap justify-between items-center gap-3 mb-8 bg-white p-4 border-4 border-black rounded-2xl shadow-brutal">
        <h1 className="font-display text-3xl sm:text-4xl">BANDIT DASHBOARD</h1>
        <div className="flex items-center gap-2">
          <button
            data-testid="toggle-alerts-btn"
            onClick={enableAlerts}
            className={`font-bold uppercase text-xs tracking-wider px-3 py-2.5 border-4 border-black rounded-xl shadow-brutal-sm flex items-center gap-2 active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform ${
              alertsOn ? "bg-candy-gold text-black" : "bg-white text-black"
            }`}
            title="Get a browser ping on every new paid order"
          >
            {alertsOn ? <BellRing className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
            <span className="hidden sm:inline">{alertsOn ? "Alerts On" : "Enable Alerts"}</span>
          </button>
          <button
            data-testid="view-storefront-btn"
            onClick={viewStorefront}
            className="bg-candy-teal text-black font-bold uppercase text-xs tracking-wider px-3 py-2.5 border-4 border-black rounded-xl shadow-brutal-sm flex items-center gap-2 active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform"
          >
            <Eye className="h-4 w-4" /> View Storefront
          </button>
          <button data-testid="admin-refresh-btn" onClick={() => load(token)} className="bg-white p-2.5 border-4 border-black rounded-xl">
            <RefreshCw className={loading ? "animate-spin" : ""} />
          </button>
          <button data-testid="admin-logout-btn" onClick={doLogout} className="bg-white p-2.5 border-4 border-black rounded-xl" title="Log out">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {statCards.map((s, i) => (
          <div key={i} className={`${s.bg} border-4 border-black rounded-2xl p-6 shadow-brutal`}>
            <s.icon className="h-8 w-8 mb-2" />
            <div className="font-display text-5xl">{s.value}</div>
            <div className="font-mono text-sm uppercase font-bold">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-testid="admin-promos">
        <div className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal" data-testid="promo-halloween2026">
          <div className="flex items-center gap-2 mb-1">
            <Ticket className="h-6 w-6 text-candy-pink" />
            <span className="font-mono text-xs uppercase font-bold tracking-widest">Free Shipping Code</span>
          </div>
          <div className="font-display text-3xl tracking-tight">
            {stats?.coupon_freeship_code || "HALLOWEEN2026"}: <span className="text-candy-pink">{stats?.coupon_freeship_uses ?? 0}</span> of {stats?.coupon_freeship_max ?? 10} used
          </div>
          <div className="mt-3 h-4 w-full bg-candy-bg border-2 border-black rounded-full overflow-hidden">
            <div
              className="h-full bg-candy-pink transition-all"
              style={{ width: `${Math.min(100, ((stats?.coupon_freeship_uses ?? 0) / (stats?.coupon_freeship_max || 10)) * 100)}%` }}
              data-testid="promo-halloween2026-bar"
            />
          </div>
          <p className="font-medium text-xs text-candy-night/60 mt-2">Free shipping for the first {stats?.coupon_freeship_max ?? 10} unique customers — one use each.</p>
        </div>

        <div className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal" data-testid="promo-spooky10">
          <div className="flex items-center gap-2 mb-1">
            <Ticket className="h-6 w-6 text-orange-500" />
            <span className="font-mono text-xs uppercase font-bold tracking-widest">Halloween Discount</span>
          </div>
          <div className="font-display text-3xl tracking-tight">
            {stats?.coupon_spooky_code || "SPOOKY10"}: <span className="text-orange-500">{stats?.coupon_spooky_uses ?? 0}</span> used
            <span className="text-candy-night/40 text-2xl"> · ${(stats?.coupon_spooky_given ?? 0).toFixed(2)} given</span>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <span className={`font-mono text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 border-2 border-black rounded-full ${stats?.coupon_spooky_active ? "bg-green-400" : "bg-black/10"}`}>
              {stats?.coupon_spooky_active ? "Active" : "Expired"}
            </span>
            <span className="font-medium text-xs text-candy-night/60">10% off Halloween items · until {stats?.coupon_spooky_expiry || "Oct 30, 2026"}</span>
          </div>
        </div>
      </div>

      <div className="bg-white border-4 border-black rounded-2xl p-4 sm:p-6 shadow-brutal mb-8" data-testid="admin-exports">
        <div className="flex items-center gap-2 mb-2">
          <Download className="h-6 w-6" />
          <h2 className="font-display text-3xl">EMAILS &amp; DATA EXPORTS</h2>
        </div>
        <p className="font-medium text-sm text-candy-night/70 mb-4">
          Everything downloads as a <b>PDF</b>. <b>Orders</b> print as full one-page invoices; the rest are clean, readable tables.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            { kind: "orders", label: "Orders", count: stats?.total_orders },
            { kind: "subscribers", label: "Waiting List", count: stats?.subscribers },
            { kind: "merch_waitlist", label: "Merch List", count: stats?.merch_waitlist },
            { kind: "restock_alerts", label: "Restock Signups", count: stats?.restock_alerts },
            { kind: "contacts", label: "Messages", count: stats?.messages },
            { kind: "spooky_uses", label: "SPOOKY10 Uses", count: stats?.coupon_spooky_uses },
          ].map((x) => (
            <a
              key={x.kind}
              data-testid={`export-${x.kind}`}
              href={exportUrl(token, x.kind)}
              className="flex flex-col items-center justify-center text-center gap-1 bg-candy-bg border-4 border-black rounded-xl p-4 shadow-brutal-sm hover:-translate-y-0.5 active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform text-candy-cream"
            >
              <Download className="h-5 w-5" />
              <span className="font-heading font-700 text-sm leading-tight">{x.label}</span>
              <span className="font-display text-2xl text-candy-pink">{x.count ?? 0}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-candy-cream/60">Download PDF</span>
            </a>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t-4 border-dashed border-black/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="font-medium text-xs text-candy-night/60 max-w-md">
            Getting ready to launch? Wipe all test orders &amp; messages so you only see real customers from here on.
          </p>
          <button
            data-testid="clear-test-data-btn"
            onClick={doPurge}
            disabled={purging}
            className="shrink-0 flex items-center justify-center gap-2 bg-candy-pink text-white font-bold uppercase text-xs tracking-wider py-2.5 px-4 border-4 border-black rounded-xl shadow-brutal-sm hover:-translate-y-0.5 active:translate-x-1 active:translate-y-1 active:shadow-none transition-transform disabled:opacity-60"
          >
            <Trash2 className="h-4 w-4" /> {purging ? "Clearing…" : "Clear Test Data"}
          </button>
        </div>
      </div>

      <div className="bg-white border-4 border-black rounded-2xl p-4 sm:p-6 shadow-brutal">
        <h2 className="font-display text-3xl mb-4">LATEST ORDERS</h2>
        <div className="overflow-x-auto rounded-xl border-2 border-black/15">
          <table className="w-full text-left border-collapse text-candy-night min-w-[520px]">
            <thead>
              <tr className="bg-candy-night text-candy-cream font-mono uppercase text-[11px] tracking-wider">
                <th className="px-4 py-3 font-bold">Customer</th>
                <th className="px-4 py-3 font-bold text-right">Total</th>
                <th className="px-4 py-3 font-bold">Fulfillment</th>
                <th className="px-4 py-3 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-candy-night/60 font-semibold">No orders yet.</td></tr>
              )}
              {orders.map((o, i) => (
                <tr key={o.id} className={`border-b-2 border-black/10 ${i % 2 ? "bg-black/[0.04]" : "bg-white"}`}>
                  <td className="px-4 py-3 align-middle">
                    <div className="font-bold text-sm leading-tight">{o.customer_name}</div>
                    <div className="font-mono text-[11px] text-candy-night/60 break-all">{o.email}</div>
                  </td>
                  <td className="px-4 py-3 align-middle text-right font-display text-2xl text-candy-pink whitespace-nowrap">${o.total?.toFixed(2)}</td>
                  <td className="px-4 py-3 align-middle uppercase font-mono text-xs font-bold whitespace-nowrap">{o.fulfillment_type || "—"}</td>
                  <td className="px-4 py-3 align-middle">
                    <select
                      data-testid={`order-status-${o.id}`}
                      value={o.status}
                      onChange={(e) => changeStatus(o.id, e.target.value)}
                      className="border-2 border-black rounded-lg px-2 py-1.5 font-mono text-xs uppercase font-bold bg-white text-candy-night"
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border-4 border-black rounded-2xl p-4 sm:p-6 shadow-brutal mt-8" data-testid="owner-inventory">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="font-display text-3xl flex items-center gap-2"><Boxes className="h-7 w-7 text-candy-pink" /> STOCK ROOM</h2>
          <span className="font-mono text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 border-2 border-black rounded-full bg-candy-gold">
            Owner only · not shown to customers
          </span>
        </div>
        <p className="font-medium text-sm text-candy-night/70 mb-5">
          Your full candy count by category. Edit any number to update it — these are private and never appear on the shop.
        </p>
        <div className="space-y-6">
          {ownerInv.length === 0 && (
            <p className="font-semibold text-candy-night/60">Loading stock room…</p>
          )}
          {ownerInv.map((g) => (
            <div key={g.category} data-testid={`stockroom-cat-${g.category}`}>
              <div className="flex items-center justify-between bg-candy-night text-candy-cream rounded-t-xl px-4 py-2">
                <h3 className="font-heading font-700 text-sm uppercase tracking-wide">{g.category}</h3>
                <span className="font-mono text-[11px] uppercase tracking-wider text-candy-cream/70">{g.total} units</span>
              </div>
              <div className="border-2 border-t-0 border-black/15 rounded-b-xl overflow-hidden">
                <table className="w-full text-left border-collapse text-candy-night">
                  <tbody>
                    {g.items.map((it, i) => (
                      <tr key={it.id} className={`border-b-2 border-black/10 last:border-b-0 ${i % 2 ? "bg-black/[0.04]" : "bg-white"}`}>
                        <td className="px-4 py-2.5 font-bold text-sm">{it.name}</td>
                        <td className="px-4 py-2.5 w-28 text-right">
                          <input
                            data-testid={`stockroom-qty-${it.id}`}
                            type="number"
                            min="0"
                            value={it.quantity}
                            onChange={(e) =>
                              setOwnerInv((prev) =>
                                prev.map((gr) => ({ ...gr, items: gr.items.map((x) => (x.id === it.id ? { ...x, quantity: e.target.value } : x)) }))
                              )
                            }
                            onBlur={(e) => setOwnerQty(it.id, e.target.value)}
                            className="w-20 bg-candy-bg text-candy-cream border-2 border-black rounded-lg px-2 py-1.5 text-sm font-bold text-center focus:outline-none focus:shadow-brutal-teal transition-shadow"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>


      <div className="bg-white border-4 border-black rounded-2xl p-4 sm:p-6 shadow-brutal mt-8" data-testid="admin-inventory">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-display text-3xl">INVENTORY &amp; RESTOCK</h2>
          <div className="flex flex-wrap gap-2">
            {["all", "in", "low", "out"].map((f) => (
              <button
                key={f}
                data-testid={`inv-filter-${f}`}
                onClick={() => setInvFilter(f)}
                className={`font-mono text-[11px] uppercase font-bold tracking-wider px-3 py-2 border-4 border-black rounded-lg transition-transform active:translate-x-0.5 active:translate-y-0.5 ${
                  invFilter === f ? "bg-candy-pink text-white shadow-brutal-sm" : "bg-white"
                }`}
              >
                {f === "all" ? "All" : f === "in" ? "In Stock" : f === "low" ? "Low" : "Sold Out"}
              </button>
            ))}
          </div>
        </div>
        <p className="font-medium text-sm text-candy-night/70 mb-4">
          Enter an <b>On Hand</b> count and stock flips <b>automatically</b> — <b>Low</b> at {"\u2264"} 5 left,
          <b> Sold Out</b> at 0 — and it drops as orders sell. Leave On Hand blank to set status by hand.
          Restocking a sold-out candy auto-emails everyone waiting. 🔔
        </p>
        <div className="overflow-x-auto rounded-xl border-2 border-black/15">
          <table className="w-full text-left border-collapse text-candy-night min-w-[620px]">
            <thead>
              <tr className="bg-candy-night text-candy-cream font-mono uppercase text-[11px] tracking-wider">
                <th className="px-4 py-3 font-bold">Product</th>
                <th className="px-4 py-3 font-bold">Category</th>
                <th className="px-4 py-3 font-bold text-center">Waiting</th>
                <th className="px-4 py-3 font-bold text-center">On Hand</th>
                <th className="px-4 py-3 font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {inventory
                .filter((p) => invFilter === "all" || (p.stock_status || "in") === invFilter)
                .map((p, i) => (
                  <tr key={p.id} className={`border-b-2 border-black/10 ${i % 2 ? "bg-black/[0.04]" : "bg-white"}`} data-testid={`inv-row-${p.id}`}>
                    <td className="px-4 py-3 align-middle">
                      <div className="flex items-center gap-3">
                        <img src={p.image} alt="" className="h-10 w-10 rounded-lg border-2 border-black object-cover shrink-0" />
                        <span className="font-bold text-sm leading-tight">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-middle font-mono text-xs font-bold uppercase text-candy-night/70 whitespace-nowrap">{p.category}</td>
                    <td className="px-4 py-3 align-middle text-center">
                      {p.waiting > 0 ? (
                        <span className="inline-flex items-center gap-1 bg-candy-gold border-2 border-black rounded-full px-2.5 py-0.5 font-mono text-[11px] font-bold">
                          <Bell className="h-3 w-3" /> {p.waiting}
                        </span>
                      ) : (
                        <span className="font-mono text-xs text-candy-night/40">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-middle text-center">
                      <input
                        data-testid={`qty-input-${p.id}`}
                        type="number"
                        min="0"
                        value={p.quantity ?? ""}
                        placeholder="—"
                        onChange={(e) =>
                          setInventory((prev) => prev.map((x) => (x.id === p.id ? { ...x, quantity: e.target.value === "" ? null : e.target.value } : x)))
                        }
                        onBlur={(e) => setQty(p.id, e.target.value)}
                        className="w-16 bg-candy-bg text-candy-cream border-2 border-black rounded-lg px-2 py-1.5 text-sm font-bold text-center focus:outline-none focus:shadow-brutal-teal transition-shadow"
                      />
                    </td>
                    <td className="px-4 py-3 align-middle">
                      {p.auto ? (
                        <div className="inline-flex flex-col items-start gap-0.5">
                          <span
                            data-testid={`stock-auto-badge-${p.id}`}
                            className={`inline-flex items-center gap-1 font-mono text-[11px] uppercase font-bold tracking-wider px-2.5 py-1.5 border-2 border-black rounded-lg ${
                              p.stock_status === "in" ? "bg-candy-teal text-black" : p.stock_status === "low" ? "bg-orange-400 text-black" : "bg-black text-white"
                            }`}
                          >
                            {p.stock_status === "in" ? <Check className="h-3.5 w-3.5" /> : p.stock_status === "low" ? <AlertTriangle className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                            {p.stock_status === "in" ? "In" : p.stock_status === "low" ? "Low" : "Out"}
                          </span>
                          <span className="font-mono text-[9px] uppercase tracking-widest text-candy-night/40">auto</span>
                        </div>
                      ) : (
                        <div className="inline-flex border-4 border-black rounded-lg overflow-hidden shadow-brutal-sm">
                          {[
                            ["in", "In", Check, "bg-candy-teal text-black"],
                            ["low", "Low", AlertTriangle, "bg-orange-400 text-black"],
                            ["out", "Out", X, "bg-black text-white"],
                          ].map(([val, label, Icon, activeCls], idx) => {
                            const current = (p.stock_status || "in") === val;
                            return (
                              <button
                                key={val}
                                data-testid={`stock-${val}-${p.id}`}
                                onClick={() => setStock(p.id, val)}
                                className={`inline-flex items-center gap-1 font-mono text-[11px] uppercase font-bold tracking-wider px-2.5 py-2 transition-colors ${idx > 0 ? "border-l-4 border-black" : ""} ${
                                  current ? activeCls : "bg-white text-black/60 hover:bg-black/5"
                                }`}
                              >
                                <Icon className="h-3.5 w-3.5" /> {label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              {inventory.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-candy-night/60 font-semibold">Loading inventory…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
